'use client'

import { SEARCH_PARAM_KEYS } from '@/constants/auth'
import { usePathname, useSearchParams } from 'next/navigation'
import { useEffect, useRef, useState } from 'react'
import { useIsClient } from 'usehooks-ts'

import ViverseCommunityDialog from '@/components/ViverseCommunityDialog'
import { useAuthContext } from '@/context/AuthContext'
import { useConfigsContext } from '@/context/ConfigsContext'
import { useVSCSdkContext } from '@/context/VSCSdkContext'
import { useCalculateHeaderHeight } from '@/hooks/useCalculateHeaderHeight'
import { useEnableFull3d } from '@/hooks/useEnableFull3d'
import { useShareComponentEvents } from '@/hooks/useShareComponentEvents'
import { useShareComponentProfile } from '@/hooks/useShareComponentProfile'
import { cn } from '@/lib/utils'

function Header() {
  const headerRef = useRef<HTMLDivElement>(null)
  const [openCommunityDialog, setOpenCommunityDialog] = useState(false)
  const pathname = usePathname()
  const isExamplePage = pathname.includes('examples')

  const { store } = useConfigsContext()
  const isClient = useIsClient()
  const { isSignedIn, handleLogin, handleLogout, isCheckingAuth } = useAuthContext()
  const { viverseShareComponent, isMountedHeader, setIsMountedHeader, changeLocale } =
    useVSCSdkContext()
  const shareComponentProfile = useShareComponentProfile()
  const enableFull3d = useEnableFull3d()
  const searchParams = useSearchParams()
  const previewMode = searchParams.has(SEARCH_PARAM_KEYS.PREVIEW)
  const communityToggle = store?.communityToggle

  useCalculateHeaderHeight(headerRef)

  useShareComponentEvents()

  useEffect(() => {
    if (isMountedHeader && viverseShareComponent && !enableFull3d && !isExamplePage) {
      viverseShareComponent.updateProfile(shareComponentProfile)
    }
  }, [
    isMountedHeader,
    viverseShareComponent,
    isSignedIn,
    shareComponentProfile,
    enableFull3d,
    isExamplePage,
  ])

  useEffect(() => {
    if (isMountedHeader || isCheckingAuth) return

    if (viverseShareComponent && headerRef.current && !enableFull3d && !isExamplePage) {
      const loginParams = previewMode ? { [SEARCH_PARAM_KEYS.PREVIEW]: '' } : {}
      viverseShareComponent.mountHeader({
        $header: headerRef.current,
        localeConfig: {
          changeLocale,
        },
        authConfig: {
          onLogin: () => {
            handleLogin({ loginParams: loginParams as Record<string, string> })
          },
          onLogout: handleLogout,
          profile: shareComponentProfile,
        },
        layoutConfig: {
          type: 6,
          community: {
            isChinaUser: !communityToggle,
            showIframe: () => setOpenCommunityDialog(true),
          },
        },
      })

      setIsMountedHeader(true)
    }
  }, [
    isExamplePage,
    enableFull3d,
    handleLogin,
    handleLogout,
    setIsMountedHeader,
    shareComponentProfile,
    viverseShareComponent,
    communityToggle,
    changeLocale,
    isMountedHeader,
    isCheckingAuth,
  ])

  return (
    <>
      <header
        ref={headerRef}
        className={cn('fixed top-0 z-50 h-auto w-full bg-transparent transition-all', {
          hidden: enableFull3d || isExamplePage,
        })}
      />

      {!enableFull3d && isClient && (
        <ViverseCommunityDialog open={openCommunityDialog} onOpenChange={setOpenCommunityDialog} />
      )}
    </>
  )
}

export default Header
